import { GetStaticPropsResult, NextPage } from 'next/types'
import {
    CoberturasProps,
    FooterIH,
    HeaderIH
} from '../../../../libs/interfaces'
import HeaderComponent from '../../../../libs/components/header'
import { Body } from '../../../../libs/components/header/Header.styled'
import { CardDS, FooterComponent, TagDS } from '../../../../libs/components'
import Metadata from '@App/libs/components/Metadata.component'
import { PageTypeEnum } from '@App/libs/interfaces'
import { decodeHtmlEntities } from '@App/libs/utils/geral'
import { EditoriaHeader } from '@App/pages/404'
import Link from 'next/link'
import {
    CEContentGrid,
    CEContentSubtitle,
    CEDisclaimer,
    CEHeader,
    CERoot,
    CETitle,
    CEUnicosContent,
    CEUnicosGrid
} from '@App/libs/style/coberturas-especiais.style'
import { CoberturaCard } from '@App/libs/components/CardCoberturaEspeciais'
import BreadcrumbComponentJU, {
    BreadHome
} from '@App/libs/components/ds/Breadcrumb'
import { memo } from 'react'
import {
    API_COBERTURAS_ESPECIAIS,
    API_HEADER_FOOTER,
    HOURS24
} from '@App/api/api'

export const coberturasDescription =
    'JOTA Coberturas Especiais - Editorias com parceiros. Séries e coberturas em profundidade sobre temas atuais'

const CoberturasEspeciaisPage: NextPage<{
    header: HeaderIH
    footer: FooterIH
    coberturas: CoberturasProps
    total: any
}> = ({ header, footer, coberturas }) => {
    const pageName = 'Coberturas Especiais'
    const todosConteudosLinks = '/coberturas-especiais/conteudos'
    const firstFourPosts = coberturas?.posts?.slice(0, 4)

    return (
        <div className='page' data-page-type='coberturas-especiais'>
            <Metadata
                url='/coberturas-especiais'
                title={pageName}
                description={coberturasDescription}
                breadcrumb={[
                    BreadHome,
                    {
                        label: pageName,
                        slug: `/coberturas-especiais`,
                        clickable: false
                    }
                ]}
                pageType={PageTypeEnum.CATEGORY}
            />

            <Body>
                {header && (
                    <HeaderComponent
                        wpMenu={header.menu}
                        ctaPRO={header.pro}
                        showAcessibility={header.enableAccessibility}
                        signupLink={header.assinePage}
                    />
                )}

                <EditoriaHeader>
                    <BreadcrumbComponentJU
                        items={[
                            BreadHome,
                            {
                                label: 'Coberturas Especiais',
                                slug: `/coberturas-especiais`,
                                clickable: false
                            }
                        ]}
                    />

                    <CETitle>{pageName}</CETitle>

                    <CEDisclaimer>
                        Conteúdo de marca com o olhar do{' '}
                        <Link href={'https://conteudo.jota.info/estudio-jota'}>
                            Estúdio JOTA
                        </Link>
                    </CEDisclaimer>

                    <CERoot>
                        <CEHeader>
                            <div>
                                <CEContentSubtitle>
                                    Editorias com parceiros
                                </CEContentSubtitle>
                                <CEDisclaimer>
                                    Séries e coberturas em profundidade sobre
                                    temas atuais
                                </CEDisclaimer>
                            </div>
                            <TagDS
                                RightIcon={'jotaMaterialArrowRight'}
                                fillIcone='jotaGray'
                                color='jotaGray'
                                insideType={'down'}
                                label={'Todos os Conteúdos'.toUpperCase()}
                                url={`${todosConteudosLinks}`}
                                size='large'
                            />
                        </CEHeader>

                        <CEContentGrid>
                            {coberturas?.temas?.map((cobertura) => (
                                <CoberturaCard
                                    key={cobertura.id}
                                    {...cobertura}
                                />
                            ))}
                        </CEContentGrid>

                        <CEHeader>
                            <div style={{ marginTop: '44px' }}>
                                <CEContentSubtitle>
                                    Conteúdos únicos
                                </CEContentSubtitle>
                            </div>
                            <TagDS
                                RightIcon={'jotaMaterialArrowRight'}
                                fillIcone='jotaGray'
                                color='jotaGray'
                                insideType={'down'}
                                label={'Todos os Conteúdos'.toUpperCase()}
                                url={`${todosConteudosLinks}`}
                                size='large'
                            />
                        </CEHeader>

                        <CEUnicosContent>
                            <CEUnicosGrid>
                                {firstFourPosts?.map((cobertura) => (
                                    <CardDS
                                        key={cobertura.id}
                                        title={
                                            cobertura.title
                                                ? decodeHtmlEntities(
                                                      cobertura.title
                                                  )
                                                : ''
                                        }
                                        titleSearchTerm={''}
                                        newsLink={() => {
                                            if (typeof window !== 'undefined') {
                                                window.open(
                                                    cobertura.link,
                                                    '_self'
                                                )
                                            }
                                        }}
                                        tagLabel={cobertura.hat}
                                        isVertical={false}
                                        tagDisableHover
                                        size='small'
                                        image={cobertura.thumbnail}
                                        imageAlt={
                                            cobertura.title
                                                ? decodeHtmlEntities(
                                                      cobertura.title
                                                  )
                                                : ''
                                        }
                                        dateTagDisableHover
                                        isSponsored
                                        subjectTagDisableHover
                                        authorTagLabel={
                                            cobertura.authors[0]?.name
                                        }
                                        multipleAuthors={[
                                            ...(cobertura.authors || [])
                                        ]}
                                        relatedTagLabel={cobertura.category}
                                        relatedTagDisableHover
                                    />
                                ))}
                            </CEUnicosGrid>
                        </CEUnicosContent>
                    </CERoot>
                </EditoriaHeader>

                {footer && <FooterComponent footer={footer} />}
            </Body>
        </div>
    )
}

export default memo(CoberturasEspeciaisPage)

/**
 * getStaticProps
 * @param {unknow} context
 * @return {Promise<GetStaticPropsResult<CoberturasEspeciaisPropsInterface>>}
 */
export async function getStaticProps(): Promise<GetStaticPropsResult<unknown>> {
    if (!API_HEADER_FOOTER || !API_COBERTURAS_ESPECIAIS) {
        throw new Error(
            'API_HEADER_FOOTER or API_COBERTURAS_ESPECIAIS is not defined'
        )
    }

    let header = null
    let footer = null
    let coberturas = null

    try {
        const [resHeaderFooter, resCoberturas] = await Promise.all([
            fetch(API_HEADER_FOOTER, { cache: 'no-cache' }),
            fetch(API_COBERTURAS_ESPECIAIS)
        ])

        if (!resCoberturas.ok) {
            throw new Error(`Erro na API: ${resCoberturas.statusText}`)
        }

        const [headerFooterJson, coberturasJson] = await Promise.all([
            resHeaderFooter.json(),
            resCoberturas.json()
        ])

        header = headerFooterJson.header
        footer = headerFooterJson.footer
        coberturas = coberturasJson
    } catch (error: any) {
        throw new Error(`Erro ao buscar dados --> ${error.message}`)
    }

    return {
        props: {
            header,
            footer,
            coberturas
        },
        revalidate: HOURS24
    }
}
